import React, { useState, useEffect, useRef } from 'react';
import { settingsAPI, usersAPI } from '../services/api';
import { Bell, Send, Trash2, Image, Link, Video, FileText, Plus, Eye, X, Mic, Square, Upload, Play, Pause, Users, Building2, User } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function NotificationManager() {
  const [notifications, setNotifications] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'text', // text, image, link, video, audio
    mediaUrl: '',
    linkUrl: '',
    linkText: '',
    targetType: 'all', // all, center, user
    targetId: ''
  });
  const [uploading, setUploading] = useState(false);
  const [previewNotif, setPreviewNotif] = useState(null);
  const [centers, setCenters] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingCibles, setLoadingCibles] = useState(false);

  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const loadedNotifications = await settingsAPI.getNotifications();
        setNotifications(loadedNotifications || []);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    };

    const loadCibles = async () => {
      setLoadingCibles(true);
      try {
        const [adminsData, usersData] = await Promise.all([
          usersAPI.getAdmins(),
          usersAPI.getAllStudents()
        ]);
        setCenters(adminsData || []);
        setAllUsers(usersData || []);
      } catch (error) {
        console.error('Error loading cibles:', error);
      } finally {
        setLoadingCibles(false);
      }
    };

    loadNotifications();
    loadCibles();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  // Start audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Enregistrement démarré');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Impossible d\'accéder au microphone. Vérifiez les permissions.');
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      toast.success('Enregistrement terminé');
    }
  };

  // Upload recorded audio
  const uploadRecordedAudio = async () => {
    if (!audioBlob) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', audioBlob, `audio_${Date.now()}.webm`);

    try {
      const response = await fetch(`${API_URL}/api/uploads/audio/`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setNewNotification(prev => ({
          ...prev,
          mediaUrl: `${API_URL}${data.url}`,
          type: 'audio'
        }));
        setAudioBlob(null);
        toast.success('Audio téléversé avec succès');
      } else {
        toast.error('Erreur lors du téléversement');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setUploading(false);
    }
  };

  // Play/pause recorded audio preview
  const togglePlayRecording = () => {
    if (!audioBlob) return;

    if (!audioPlayerRef.current) {
      audioPlayerRef.current = new Audio(URL.createObjectURL(audioBlob));
      audioPlayerRef.current.onended = () => setIsPlaying(false);
    }

    if (isPlaying) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    } else {
      audioPlayerRef.current.play();
      setIsPlaying(true);
    }
  };

  // Cancel recording
  const cancelRecording = () => {
    setAudioBlob(null);
    setRecordingTime(0);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    setIsPlaying(false);
  };

  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSizes = {
      video: 50 * 1024 * 1024,  // 50MB
      image: 5 * 1024 * 1024,   // 5MB
      audio: 10 * 1024 * 1024   // 10MB
    };
    
    const maxSize = maxSizes[type] || 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Fichier trop volumineux (max ${maxSize / (1024 * 1024)}MB)`);
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoints = {
        video: '/api/uploads/video/',
        image: '/api/uploads/image/',
        audio: '/api/uploads/audio/'
      };
      const endpoint = endpoints[type] || '/api/uploads/image/';
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setNewNotification(prev => ({
          ...prev,
          mediaUrl: `${API_URL}${data.url}`,
          type: type
        }));
        const typeLabels = { video: 'Vidéo', image: 'Image', audio: 'Audio' };
        toast.success(`${typeLabels[type] || 'Fichier'} téléversé avec succès`);
      } else {
        toast.error('Erreur lors du téléversement');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newNotification.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (!newNotification.message.trim() && newNotification.type === 'text') {
      toast.error('Le message est requis');
      return;
    }
    if (newNotification.type === 'audio' && !newNotification.mediaUrl) {
      toast.error('Veuillez enregistrer ou uploader un audio');
      return;
    }

    try {
      const result = await settingsAPI.createNotification({
        title: newNotification.title,
        message: newNotification.message,
        type: newNotification.type,
        media_url: newNotification.mediaUrl,
        link_url: newNotification.linkUrl,
        link_text: newNotification.linkText,
        target_type: newNotification.targetType,
        target_id: newNotification.targetId
      });
      
      if (result.success) {
        // Add to local state
        if (result.data) {
          setNotifications([result.data, ...notifications]);
        } else {
          // Reload if no data returned
          const loadedNotifications = await settingsAPI.getNotifications();
          setNotifications(loadedNotifications || []);
        }
        
        setNewNotification({
          title: '',
          message: '',
          type: 'text',
          mediaUrl: '',
          linkUrl: '',
          linkText: '',
          targetType: 'all',
          targetId: ''
        });
        setAudioBlob(null);
        setShowForm(false);
        toast.success('Notification envoyée à tous les utilisateurs !');
      } else {
        toast.error(result.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error creating notification:', error);
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleDelete = async (notifId) => {
    if (!window.confirm('Supprimer cette notification ?')) return;
    
    try {
      await settingsAPI.deleteNotification(notifId);
      setNotifications(notifications.filter(n => n.id !== notifId));
      toast.success('Notification supprimée');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'image': return <Image className="h-4 w-4" />;
      case 'link': return <Link className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      case 'audio': return <Mic className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'image': return 'Image';
      case 'link': return 'Lien';
      case 'video': return 'Vidéo';
      case 'audio': return 'Audio';
      default: return 'Texte';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6 text-primary" />
            Notifications globales
          </h2>
          <p className="text-muted-foreground mt-1">
            Envoyez des messages, images, liens, vidéos ou vocaux à tous les utilisateurs
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="new-notification-btn"
          className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Nouvelle notification</span>
        </button>
      </div>

      {/* New Notification Form */}
      {showForm && (
        <div className="bg-card rounded-xl border p-6 space-y-4">
          <h3 className="font-semibold text-lg">Créer une notification</h3>
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">Titre *</label>
            <input
              type="text"
              value={newNotification.title}
              onChange={(e) => setNewNotification(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Titre de la notification"
              data-testid="notif-title-input"
              className="w-full px-4 py-2 rounded-lg border bg-background"
            />
          </div>

          {/* Targeting */}
          <div className="space-y-3">
            <label className="block text-sm font-medium">Destinataires</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setNewNotification(prev => ({ ...prev, targetType: 'all', targetId: '' }))}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
                  newNotification.targetType === 'all'
                    ? 'bg-primary/20 text-primary border-primary/30'
                    : 'hover:bg-secondary'
                }`}
              >
                <Users className="h-4 w-4" />
                <span>Tous</span>
              </button>
              <button
                onClick={() => setNewNotification(prev => ({ ...prev, targetType: 'center', targetId: '' }))}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
                  newNotification.targetType === 'center'
                    ? 'bg-primary/20 text-primary border-primary/30'
                    : 'hover:bg-secondary'
                }`}
              >
                <Building2 className="h-4 w-4" />
                <span>Un Centre</span>
              </button>
              <button
                onClick={() => setNewNotification(prev => ({ ...prev, targetType: 'user', targetId: '' }))}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
                  newNotification.targetType === 'user'
                    ? 'bg-primary/20 text-primary border-primary/30'
                    : 'hover:bg-secondary'
                }`}
              >
                <User className="h-4 w-4" />
                <span>Un Elève</span>
              </button>
            </div>

            {/* Target Selectors */}
            {newNotification.targetType === 'center' && (
              <div className="animate-in fade-in slide-in-from-top-1">
                <select
                  value={newNotification.targetId}
                  onChange={(e) => setNewNotification(prev => ({ ...prev, targetId: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border bg-background"
                >
                  <option value="">Sélectionner un centre...</option>
                  {centers.map(admin => (
                    <option key={admin.id} value={admin.id}>
                      {admin.nom || admin.username} ({admin.invitation_code})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {newNotification.targetType === 'user' && (
              <div className="animate-in fade-in slide-in-from-top-1">
                <select
                  value={newNotification.targetId}
                  onChange={(e) => setNewNotification(prev => ({ ...prev, targetId: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border bg-background"
                >
                  <option value="">Sélectionner un élève...</option>
                  {allUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.nom || u.username} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Type de contenu</label>
            <div className="flex flex-wrap gap-2">
              {['text', 'image', 'link', 'video', 'audio'].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setNewNotification(prev => ({ ...prev, type, mediaUrl: '', linkUrl: '' }));
                    setAudioBlob(null);
                    cancelRecording();
                  }}
                  data-testid={`notif-type-${type}`}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
                    newNotification.type === type
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'hover:bg-secondary'
                  }`}
                >
                  {getTypeIcon(type)}
                  <span>{getTypeLabel(type)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Message (for all types) */}
          <div>
            <label className="block text-sm font-medium mb-2">Message</label>
            <textarea
              value={newNotification.message}
              onChange={(e) => setNewNotification(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Votre message..."
              rows={4}
              data-testid="notif-message-input"
              className="w-full px-4 py-2 rounded-lg border bg-background resize-none"
            />
          </div>

          {/* Image Upload */}
          {newNotification.type === 'image' && (
            <div>
              <label className="block text-sm font-medium mb-2">Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'image')}
                className="w-full px-4 py-2 rounded-lg border bg-background"
                disabled={uploading}
              />
              {newNotification.mediaUrl && (
                <div className="mt-2">
                  <img src={newNotification.mediaUrl} alt="Preview" className="max-h-40 rounded-lg" />
                </div>
              )}
            </div>
          )}

          {/* Video Upload */}
          {newNotification.type === 'video' && (
            <div>
              <label className="block text-sm font-medium mb-2">Vidéo (max 50MB)</label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => handleFileUpload(e, 'video')}
                className="w-full px-4 py-2 rounded-lg border bg-background"
                disabled={uploading}
              />
              {newNotification.mediaUrl && (
                <div className="mt-2">
                  <video src={newNotification.mediaUrl} controls className="max-h-40 rounded-lg" />
                </div>
              )}
            </div>
          )}

          {/* Audio - Record or Upload */}
          {newNotification.type === 'audio' && (
            <div className="space-y-4">
              <label className="block text-sm font-medium">Message vocal</label>
              
              {/* Recording Controls */}
              <div className="bg-secondary/50 rounded-xl p-4 space-y-4">
                {!audioBlob && !newNotification.mediaUrl ? (
                  <>
                    {/* Record Button */}
                    <div className="flex items-center justify-center space-x-4">
                      {!isRecording ? (
                        <button
                          onClick={startRecording}
                          disabled={uploading}
                          data-testid="start-recording-btn"
                          className="flex items-center space-x-2 px-6 py-3 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                        >
                          <Mic className="h-5 w-5" />
                          <span>Enregistrer un vocal</span>
                        </button>
                      ) : (
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2 px-4 py-2 rounded-full bg-red-100 dark:bg-red-900/30">
                            <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
                            <span className="font-mono text-red-600 dark:text-red-400">{formatTime(recordingTime)}</span>
                          </div>
                          <button
                            onClick={stopRecording}
                            data-testid="stop-recording-btn"
                            className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gray-800 text-white hover:bg-gray-900 transition-colors"
                          >
                            <Square className="h-4 w-4" />
                            <span>Arrêter</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="flex items-center space-x-4">
                      <div className="flex-1 border-t" />
                      <span className="text-sm text-muted-foreground">ou</span>
                      <div className="flex-1 border-t" />
                    </div>

                    {/* Upload Audio File */}
                    <div>
                      <label className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg border-2 border-dashed cursor-pointer hover:bg-secondary/50 transition-colors">
                        <Upload className="h-5 w-5 text-muted-foreground" />
                        <span className="text-muted-foreground">Uploader un fichier audio (max 10MB)</span>
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={(e) => handleFileUpload(e, 'audio')}
                          className="hidden"
                          disabled={uploading || isRecording}
                        />
                      </label>
                    </div>
                  </>
                ) : (
                  /* Audio Preview */
                  <div className="space-y-3">
                    {audioBlob && !newNotification.mediaUrl && (
                      <>
                        <div className="flex items-center justify-center space-x-4">
                          <button
                            onClick={togglePlayRecording}
                            className="flex items-center space-x-2 px-4 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                          >
                            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                            <span>{isPlaying ? 'Pause' : 'Écouter'}</span>
                          </button>
                          <span className="text-sm text-muted-foreground">Durée: {formatTime(recordingTime)}</span>
                        </div>
                        
                        <div className="flex items-center justify-center space-x-3">
                          <button
                            onClick={uploadRecordedAudio}
                            disabled={uploading}
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors disabled:opacity-50"
                          >
                            <Upload className="h-4 w-4" />
                            <span>{uploading ? 'Téléversement...' : 'Valider cet enregistrement'}</span>
                          </button>
                          <button
                            onClick={cancelRecording}
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
                          >
                            <X className="h-4 w-4" />
                            <span>Annuler</span>
                          </button>
                        </div>
                      </>
                    )}

                    {newNotification.mediaUrl && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-center">
                          <audio src={newNotification.mediaUrl} controls className="w-full max-w-md" />
                        </div>
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => {
                              setNewNotification(prev => ({ ...prev, mediaUrl: '' }));
                              cancelRecording();
                            }}
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg border hover:bg-secondary transition-colors text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Supprimer et réenregistrer</span>
                          </button>
                        </div>
                        <p className="text-center text-sm text-green-600">Audio prêt à être envoyé !</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Link Input */}
          {newNotification.type === 'link' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-2">URL du lien</label>
                <input
                  type="url"
                  value={newNotification.linkUrl}
                  onChange={(e) => setNewNotification(prev => ({ ...prev, linkUrl: e.target.value }))}
                  placeholder="https://..."
                  data-testid="notif-link-url"
                  className="w-full px-4 py-2 rounded-lg border bg-background"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Texte du bouton</label>
                <input
                  type="text"
                  value={newNotification.linkText}
                  onChange={(e) => setNewNotification(prev => ({ ...prev, linkText: e.target.value }))}
                  placeholder="Cliquez ici"
                  data-testid="notif-link-text"
                  className="w-full px-4 py-2 rounded-lg border bg-background"
                />
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => {
                setShowForm(false);
                cancelRecording();
              }}
              className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={uploading || isRecording}
              data-testid="send-notification-btn"
              className="flex items-center space-x-2 px-6 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              <span>Envoyer</span>
            </button>
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="bg-card rounded-xl border">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Notifications envoyées ({notifications.length})</h3>
        </div>
        
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Aucune notification envoyée</p>
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map((notif) => (
              <div key={notif.id} className="p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        notif.type === 'image' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        notif.type === 'link' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        notif.type === 'video' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                        notif.type === 'audio' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {getTypeLabel(notif.type)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(notif.created_at || notif.createdAt).toLocaleString('fr-FR')}
                      </span>
                      {notif.target_type && notif.target_type !== 'all' && (
                        <span className="px-2 py-0.5 rounded text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                          {notif.target_type === 'center' ? 'CENTRE' : 'INDIVIDUEL'}
                        </span>
                      )}
                    </div>
                    <h4 className="font-semibold">{notif.title}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">{notif.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Lu par {notif.read_by?.length || notif.readBy?.length || 0} utilisateur(s)
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => setPreviewNotif(notif)}
                      className="p-2 rounded-lg hover:bg-secondary transition-colors"
                      title="Aperçu"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(notif.id)}
                      className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewNotif && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Aperçu de la notification</h3>
              <button
                onClick={() => setPreviewNotif(null)}
                className="p-2 rounded-lg hover:bg-secondary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <h2 className="text-xl font-bold">{previewNotif.title}</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">{previewNotif.message}</p>
              
              {previewNotif.type === 'image' && (previewNotif.media_url || previewNotif.mediaUrl) && (
                <img src={previewNotif.media_url || previewNotif.mediaUrl} alt="" className="w-full rounded-lg" />
              )}
              
              {previewNotif.type === 'video' && (previewNotif.media_url || previewNotif.mediaUrl) && (
                <video src={previewNotif.media_url || previewNotif.mediaUrl} controls className="w-full rounded-lg" />
              )}

              {previewNotif.type === 'audio' && (previewNotif.media_url || previewNotif.mediaUrl) && (
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <Mic className="h-5 w-5 text-orange-500" />
                    <span className="font-medium">Message vocal</span>
                  </div>
                  <audio src={previewNotif.media_url || previewNotif.mediaUrl} controls className="w-full" />
                </div>
              )}
              
              {previewNotif.type === 'link' && (previewNotif.link_url || previewNotif.linkUrl) && (
                <a
                  href={(() => {
                    const url = previewNotif.link_url || previewNotif.linkUrl;
                    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                      return 'https://' + url;
                    }
                    return url;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Link className="h-4 w-4" />
                  <span>{previewNotif.link_text || previewNotif.linkText || 'Ouvrir le lien'}</span>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
